import type {IssueReferenceStatus} from '@github-ui/issue-pr-state/IssueStatusIcon'

import type {SavedViewsBoardItemAxisMove} from '../types'
import type {IssuesBoardMetadata} from './board-metadata'
import {
  type BoardAxisLabels,
  type BoardPayloadIssueItem,
  type BoardPayloadIssueStatus,
  type BoardPayloadItem,
  type IssueState,
  type IssueStateReason,
  NO_VALUE_KEY,
} from './board-payload'

/** The wire's lifecycle in the casing `@github-ui/issue-pr-state` reads. */
const ISSUE_STATES = {open: 'OPEN', closed: 'CLOSED'} as const satisfies Record<IssueState, string>
const ISSUE_STATE_REASONS = {
  completed: 'COMPLETED',
  not_planned: 'NOT_PLANNED',
  duplicate: 'DUPLICATE',
  reopened: 'REOPENED',
} as const satisfies Record<IssueStateReason, string>

type IssueLifecycle = Pick<BoardPayloadIssueItem, 'state' | 'stateReason' | 'status'>

/** A status as `IssueStatusIcon` reads it. */
export function toIssueStatusIconStatus(status: BoardPayloadIssueStatus): IssueReferenceStatus {
  return {
    name: status.name,
    category: {
      key: status.category,
      state: status.state === 'open' ? 'OPEN' : 'CLOSED',
      stateReason: status.stateReason?.toUpperCase() ?? null,
    },
  }
}

/** An issue as `IssueReferenceIcon` reads it, for a card or a parent issue. */
export function toIssueReference({state, stateReason, status}: IssueLifecycle) {
  return {
    state: ISSUE_STATES[state],
    stateReason: stateReason === null ? null : ISSUE_STATE_REASONS[stateReason],
    status: status == null ? null : toIssueStatusIconStatus(status),
  }
}

/**
 * Gives an issue card the status of the bucket a move put it in, along with the state that
 * status sets, so the card matches its bucket until the next board read. A move into the
 * no-value bucket of a status axis clears the status and keeps the state. Any other move leaves
 * the card as it was.
 */
export function updateMovedIssueItem(
  item: BoardPayloadItem,
  {axis, keys}: SavedViewsBoardItemAxisMove,
  {columnLabels, rowLabels}: IssuesBoardMetadata,
): BoardPayloadItem {
  const [key] = keys
  if (item.type !== 'issue' || key === undefined || keys.length !== 1) return item

  const labels = axis === 'columns' ? columnLabels : rowLabels
  if (key === NO_VALUE_KEY) return item.status != null && isIssueStatusAxis(labels) ? {...item, status: null} : item

  const status = labels.get(key)?.issueStatus
  if (status === undefined) return item

  if (status.state === 'open') {
    return {...item, status, state: 'open', stateReason: item.state === 'closed' ? 'reopened' : item.stateReason}
  }

  const stateReason = closedStateReason(status)
  // `matchingIssueStatus` hides a status whose reason differs from the issue reason.
  return {...item, status: {...status, stateReason}, state: 'closed', stateReason}
}

export function isIssueStatusAxis(labels: BoardAxisLabels): boolean {
  for (const label of labels.values()) {
    if (label.issueStatus !== undefined) return true
  }
  return false
}

/**
 * The reason a closed status closes an issue for. Read from the category when the server does not
 * send `stateReason` yet. Completed is the default, as in GraphQL.
 */
function closedStateReason({stateReason, category}: BoardPayloadIssueStatus): IssueStateReason {
  if (isClosedStateReason(stateReason)) return stateReason
  return isClosedStateReason(category) ? category : 'completed'
}

function isClosedStateReason(value: string | null | undefined): value is IssueStateReason {
  return value === 'completed' || value === 'not_planned' || value === 'duplicate'
}
